import React, { useCallback, useEffect, useRef, useState } from 'react';
import IconButton from './IconButton';
import InputText from './InputText';
import InputTextArea from './InputTextArea';
import InputNumber from './InputNumber';
import InputSelect from './InputSelect';
import type { SelectOption } from './InputSelect';
import SaveStatusFeedback, { SaveStatus } from './SaveStatusFeedback';
import MenuButton from './MenuButton';
import { StockCategoryEntity, StockEntity, StockPlaceEntity } from '../types/data';
import './DrawerStockDetail.css';

interface DrawerStockDetailProps {
  stock?: StockEntity;
  isOpen: boolean;
  onClose: () => void;
  onSave?: (stock: StockEntity) => void;
  onCreate?: (stock: Omit<StockEntity, 'id'>) => void;
  onDelete?: (stockId: string) => void;
  onPreviousStock?: () => void;
  onNextStock?: () => void;
  hasPreviousStock?: boolean;
  hasNextStock?: boolean;
  /** 親から提供されるカテゴリ一覧（未削除のみ） */
  activeStockCategories: StockCategoryEntity[];
  /** 親から提供される場所一覧（未削除のみ） */
  activeStockPlaces: StockPlaceEntity[];
  /** 親のフック経由でカテゴリを作成（親側の状態を更新するため） */
  createStockCategory: (
    categoryData: Omit<StockCategoryEntity, 'id' | 'created_at' | 'modified_at' | 'is_deleted'>
  ) => Promise<StockCategoryEntity> | StockCategoryEntity;
  /** 親のフック経由で場所を作成 */
  createStockPlace: (
    placeData: Omit<StockPlaceEntity, 'id' | 'created_at' | 'modified_at' | 'is_deleted'>
  ) => Promise<StockPlaceEntity> | StockPlaceEntity;
}

const DrawerStockDetail: React.FC<DrawerStockDetailProps> = ({
  stock,
  isOpen,
  onClose,
  onSave,
  onCreate,
  onDelete,
  onPreviousStock,
  onNextStock,
  hasPreviousStock = false,
  hasNextStock = false,
  activeStockCategories,
  activeStockPlaces,
  createStockCategory,
  createStockPlace,
}) => {
  const [localStock, setLocalStock] = useState<StockEntity | null>(null);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  const [saveError, setSaveError] = useState<string | undefined>();
  const [isCreated, setIsCreated] = useState(false);

  const isNew = stock?.id === '' && !isCreated;

  const drawerRef = useRef<HTMLDivElement>(null);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const successTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isCreatingRef = useRef(false);
  const isComposingRef = useRef(false);
  const prevIdRef = useRef<string | null>(null);

  const categoryOptions: SelectOption[] = [
    { value: '', label: '未分類' },
    ...activeStockCategories
      .sort((a, b) => {
        const ao = a.sort_order ?? 0;
        const bo = b.sort_order ?? 0;
        if (ao !== bo) return ao - bo;
        return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
      })
      .map(c => ({ value: c.id, label: c.title }))
  ];

  const placeOptions: SelectOption[] = [
    { value: '', label: '未設定' },
    ...activeStockPlaces
      .sort((a, b) => {
        const ao = a.sort_order ?? 0;
        const bo = b.sort_order ?? 0;
        if (ao !== bo) return ao - bo;
        return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
      })
      .map(p => ({ value: p.id, label: p.title }))
  ];

  const clearSuccessMessage = useCallback(() => {
    if (successTimeoutRef.current) clearTimeout(successTimeoutRef.current);
    successTimeoutRef.current = setTimeout(() => setSaveStatus('idle'), 2000);
  }, []);

  const hasNewInput = useCallback((s: StockEntity) => {
    return (s.title?.trim() ?? '') !== '' || (s.body?.trim() ?? '') !== '' || (s.where_to_get?.trim() ?? '') !== '';
  }, []);

  const performSave = useCallback(async (s: StockEntity) => {
    try {
      setSaveStatus('saving');
      setSaveError(undefined);

      if (isNew && isComposingRef.current) {
        setSaveStatus('dirty');
        return;
      }

      if (isNew) {
        if (!hasNewInput(s)) {
          setSaveStatus('idle');
          return;
        }
        if (isCreatingRef.current) return;
        isCreatingRef.current = true;
        try {
          const { id, ...withoutId } = s as StockEntity & { id: string };
          await Promise.resolve(onCreate?.(withoutId));
          setIsCreated(true);
        } finally {
          isCreatingRef.current = false;
        }
      } else {
        await Promise.resolve(onSave?.(s));
      }

      setSaveStatus('success');
      clearSuccessMessage();
    } catch (error) {
      setSaveStatus('error');
      setSaveError(error instanceof Error ? error.message : '保存に失敗しました');
    }
  }, [isNew, hasNewInput, onCreate, onSave, clearSuccessMessage]);

  const debouncedSave = useCallback((s: StockEntity) => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    setSaveStatus('dirty');
    debounceTimerRef.current = setTimeout(() => performSave(s), 500);
  }, [performSave]);

  useEffect(() => {
    const currentId = stock?.id ?? null;
    const hasChanged = prevIdRef.current !== currentId;
    if (hasChanged || !localStock) {
      if (stock) {
        setLocalStock({ ...stock });
        setIsCreated(stock.id !== '');
        prevIdRef.current = currentId;
      } else {
        setLocalStock(null);
        setIsCreated(false);
        prevIdRef.current = null;
      }
      setSaveStatus('idle');
      setSaveError(undefined);
    }
  }, [stock, localStock]);

  useEffect(() => () => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    if (successTimeoutRef.current) clearTimeout(successTimeoutRef.current);
  }, []);

  useEffect(() => {
    if (isOpen && drawerRef.current) drawerRef.current.scrollTop = 0;
  }, [isOpen]);

  const updateLocal = (patch: Partial<StockEntity>) => {
    if (!localStock) return;
    const updated: StockEntity = { ...localStock, ...patch, modified_at: new Date().toISOString() };
    setLocalStock(updated);
    debouncedSave(updated);
  };

  const handleCompositionStart = () => { isComposingRef.current = true; };
  const handleCompositionEnd = () => { isComposingRef.current = false; };

  const handleIncrement = () => {
    if (!localStock) return;
    const current = localStock.count ?? 0;
    updateLocal({ count: current + 1 });
  };
  const handleDecrement = () => {
    if (!localStock) return;
    const current = localStock.count ?? 0;
    const next = Math.max(0, current - 1);
    updateLocal({ count: next });
  };

  const handleDelete = () => {
    if (!localStock || isNew) return;
    onDelete?.(localStock.id);
  };


  return (
    <div className={`drawer-stock-detail ${isOpen ? 'drawer-stock-detail--open' : ''}`}>
      <div
        ref={drawerRef}
        className="drawer-stock-detail__drawer"
        role="dialog"
        aria-modal="true"
        aria-labelledby="drawer-stock-title"
      >
        {localStock && (
          <>
            <div className="drawer-stock-detail__header">
              <div className="drawer-stock-detail__header-left">
                <IconButton
                  icon="close"
                  onClick={onClose}
                  aria-label="閉じる"
                  tooltip="閉じる"
                  tooltipPosition="bottom-right"
                />
                <IconButton
                  icon="chevronUp"
                  onClick={() => onPreviousStock?.()}
                  disabled={!hasPreviousStock}
                  aria-label="前へ"
                  tooltip="前へ"
                  tooltipPosition="bottom-right"
                />
                <IconButton
                  icon="chevronDown"
                  onClick={() => onNextStock?.()}
                  disabled={!hasNextStock}
                  aria-label="次へ"
                  tooltip="次へ"
                  tooltipPosition="bottom-right"
                />
              </div>
              <div className="drawer-stock-detail__header-center">
                <SaveStatusFeedback status={saveStatus} errorMessage={saveError} />
              </div>
              <div className="drawer-stock-detail__header-right">
                {!isNew && (
                  <MenuButton
                    icon="menu"
                    aria-label="ストック操作メニュー"
                    tooltip="ストック操作メニュー"
                    tooltipPosition="bottom-left"
                    items={[{
                      type: 'Destructive',
                      label: 'ストックを削除',
                      leftIcon: 'delete',
                      showLeftIcon: true,
                      onClick: handleDelete
                    }]}
                  />
                )}
              </div>
            </div>

            <div className="drawer-stock-detail__content">
              <div className="drawer-stock-detail__field">
                <InputText
                  hasLeftIcon={false}
                  placeholder="タイトルを入力"
                  value={localStock.title}
                  onChange={e => updateLocal({ title: e.target.value })}
                  onCompositionStart={handleCompositionStart}
                  onCompositionEnd={handleCompositionEnd}
                />
              </div>

              <div className="drawer-stock-detail__field">
                <InputText
                  hasLeftIcon={true}
                  leftIcon="shop"
                  placeholder="手に入れる場所を入力（任意）"
                  value={localStock.where_to_get || ''}
                  onChange={e => updateLocal({ where_to_get: e.target.value })}
                  onCompositionStart={handleCompositionStart}
                  onCompositionEnd={handleCompositionEnd}
                />
              </div>

              <div className="drawer-stock-detail__field">
                <InputNumber
                  hasLeftIcon={true}
                  leftIcon="inventory"
                  placeholder="在庫数"
                  value={localStock.count ?? 0}
                  min={0}
                  step={1}
                  hideSpinner={false}
                  width={120}
                  onChange={e => updateLocal({ count: e.target.value === '' ? 0 : Number(e.target.value) })}
                />
                <div className="drawer-stock-detail__field__scock-manager" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
                  <IconButton
                    icon="minus"
                    onClick={handleDecrement}
                    tooltip="ストック数を減らす"
                    tooltipPosition="bottom-left"
                    aria-label="ストック数を減らす"
                    disabled={(localStock.count ?? 0) <= 0}
                  />
                  <IconButton
                    icon="add"
                    onClick={handleIncrement}
                    tooltip="ストック数を増やす"
                    tooltipPosition="bottom-left"
                    aria-label="ストック数を増やす"
                  />
                </div>
              </div>

              <div className="drawer-stock-detail__field">
                <InputSelect
                  hasLeftIcon={true}
                  leftIcon="folder"
                  placeholder="カテゴリ"
                  mode="combobox"
                  value={localStock.stock_category_id || ''}
                  options={categoryOptions}
                  onValueChange={(v) => { updateLocal({ stock_category_id: v || undefined }); }}
                  onCreateOption={async (label) => {
                    const created = await createStockCategory({ title: label, body: '' });
                    return { value: created.id, label: created.title };
                  }}
                />
              </div>

              <div className="drawer-stock-detail__field">
                <InputSelect
                  hasLeftIcon={true}
                  leftIcon="location"
                  placeholder="保管場所"
                  mode="combobox"
                  value={localStock.stock_place_id || ''}
                  options={placeOptions}
                  onValueChange={(v) => { updateLocal({ stock_place_id: v || undefined }); }}
                  onCreateOption={async (label) => {
                    const created = await createStockPlace({ title: label, body: '' });
                    return { value: created.id, label: created.title };
                  }}
                />
              </div>

              <div className="drawer-stock-detail__field">
                <InputTextArea
                  placeholder="メモを入力（任意）"
                  value={localStock.body || ''}
                  onChange={e => updateLocal({ body: e.target.value })}
                  onCompositionStart={handleCompositionStart}
                  onCompositionEnd={handleCompositionEnd}
                  rows={6}
                />
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default DrawerStockDetail;
